import React, { useEffect, useState, useMemo } from 'react';
import { macroService } from '../../services';
import Card from '../../components/Card/Card';
import Button from '../../components/buttons/Button';
import BackButton from '../../components/buttons/BackButton';
import useNotification from '../../components/Notifications/useNotifications.js';
import Notification from '../../components/Notifications/Notification.js';
import { FaPlus, FaSave, FaTimes, FaTrash, FaKeyboard } from 'react-icons/fa';
import './MacroWindow.css';
import SkeletonManager from '../../components/loaders/SkeletonManager';
import HoverTooltip from '../../components/tooltips/HoverTooltip';
import { useAuth } from '../../context/AuthContext';
import { createLogger } from '../../services/Debug';
function MacroWindow() {
  const { profile } = useAuth();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [macros, setMacros] = useState([]);
  const [original, setOriginal] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { notification, showNotification, hideNotification } = useNotification();
  const log = createLogger('MacroWindow');
  const filteredMacros = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return macros
      .map((m, idx) => ({ m, idx }))
      .filter(({ m }) => {
        if (!q) return true;
        const key = (m.keyword || '').toLowerCase();
        const content = (m.expansionText || '').toLowerCase();
        return key.includes(q) || content.includes(q);
      });
  }, [macros, searchQuery]);

  const loadMacros = async () => {
    const minimumLoadingTime = 500;
    const startTime = Date.now();
    try {
      
      const list = await macroService.getMacros();
      const plain = list.map(m => ({ id: m.id, keyword: m.keyword, expansionText: m.expansionText }));
      setMacros(plain);
      setOriginal(plain);
      localStorage.setItem('userMacros', JSON.stringify(plain));

      // Sync to Electron main (key/content)
      const electronMacros = plain.map(m=>({ key: m.keyword, content: m.expansionText }));
      window.electronAPI?.saveMacros?.(electronMacros);

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } catch (err) {
      log.error('Error fetching macros', err);
      setMacros([]);

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    loadMacros();
    const handler = () => loadMacros();
    window.addEventListener('focus', handler);
    const vis = () => { if (!document.hidden) loadMacros(); };
    document.addEventListener('visibilitychange', vis);
    return () => {
      window.removeEventListener('focus', handler);
      document.removeEventListener('visibilitychange', vis);
    };
  }, []);

  // Editor-only window (insertion handled by MacroSearchWindow)

  const addMacro = () => {
    if (!isGoldOrEnterprise) return;
    const newMacro = { id: null, keyword: '', expansionText: '' };
    setMacros(ms => [newMacro, ...ms]);
    setExpandedIndex(0);
    setHasChanges(true);
    setSearchQuery(''); // Clear search when adding a new macro
  };

  const updateKey = (index, value) => {
    value = value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    setMacros(ms => ms.map((m,i)=> i===index ? { ...m, keyword: value } : m));
    setHasChanges(true);
  };

  const updateContent = (index, value) => {
    setMacros(ms => ms.map((m,i)=> i===index ? { ...m, expansionText: value } : m));
    setHasChanges(true);
  };

  const deleteMacro = (index) => {
    if (!isGoldOrEnterprise) return;
    setMacros(ms => {
      const next = ms.filter((_,i)=>i!==index);
      // Adjust expanded index if needed
      if (expandedIndex !== null) {
        if (index === expandedIndex) {
          setExpandedIndex(null);
        } else if (index < expandedIndex) {
          setExpandedIndex(expandedIndex - 1);
        }
      }
      return next;
    });
    setHasChanges(true);
  };

  const saveMacros = async () => {
    if (!isGoldOrEnterprise) return;
    try {
      const valid = macros.filter(m => m.keyword && /^[a-zA-Z0-9]+$/.test(m.keyword) && m.expansionText.trim());
      const keys = valid.map(m => m.keyword.toLowerCase());
      const hasDup = keys.some((k,i)=> keys.indexOf(k)!==i);
      if (hasDup) {
        alert('Des mots-clés sont en double. Veuillez corriger avant de sauvegarder.');
        return;
      }

      // Determine diff
      const originalIds = new Set(original.filter(m=>m.id).map(m=>m.id));
      const currentIds = new Set(valid.filter(m=>m.id).map(m=>m.id));

      // Deletes
      for (const id of originalIds) {
        if (!currentIds.has(id)) {
          await macroService.deleteMacro(id);
        }
      }

      // Creates & updates
      for (const m of valid) {
        if (m.id) {
          // find original
          const orig = original.find(o=>o.id===m.id);
          if (!orig || orig.keyword!==m.keyword || orig.expansionText!==m.expansionText) {
            await macroService.updateMacro(m.id, { keyword: m.keyword, expansionText: m.expansionText });
            showNotification('Macro mise à jour', 'success');
          }
        } else {
          await macroService.createMacro({ keyword: m.keyword, expansionText: m.expansionText });
          showNotification('Macro créée', 'success');
        }
      }

      await loadMacros();
      // After API sync, also update Electron store
      const electronMacros = valid.map(m=>({ key: m.keyword, content: m.expansionText }));
      await window.electronAPI.saveMacros(electronMacros);
      setHasChanges(false);
    } catch (err) {
      log.error('Error saving macros', err);
      alert('Erreur lors de la sauvegarde des macros');
    }
  };

  const revertChanges = () => {
    // Restore the macros collection from original state
    const restored = original.map(m => ({ id: m.id, keyword: m.keyword, expansionText: m.expansionText }));
    setMacros(restored);
    setExpandedIndex(null);
    setHasChanges(false);
  };

  const closeWindow = async () => {
    if (hasChanges) {
      await saveMacros();
    }
    window.electronAPI?.hideMacroWindow?.();
  };

  const isGoldOrEnterprise = useMemo(() => {
    return Boolean(
      profile?.isGoldOrEnterprise === true ||
      profile?.isgoldorenterprise === true ||
      profile?.subscription?.planType === 'gold' ||
      profile?.subscription?.planType === 'enterprise' ||
      profile?.subscription?.plan === 'gold' ||
      profile?.subscription?.plan === 'enterprise'
    );
  }, [profile]);
  const gatingText = 'Réservé aux abonnements Gold et Entreprise';

  if (isInitialLoading) {
    return (
      <SkeletonManager  active={true} />
    );
  }

  return (
    
    <div className="macro-container">
           {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}

      <Card className="card-large">
      <BackButton to="/home" />
        <div className="macro-content">
          {/* Action Buttons */}
          <div className="settings-section-header">
          <div className="settings-section-icon-wrapper languages">
            <FaKeyboard className="settings-section-icon" />
          </div>
          <h2>Macros</h2>
        </div>
          <div className="macro-actions">
            <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
              <span>
                <Button
                  variant="primary"
                  size="medium"
                  icon={FaPlus}
                  onClick={addMacro}
                  disabled={!isGoldOrEnterprise}
                >
                  Ajouter une macro
                </Button>
              </span>
            </HoverTooltip>
            <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
              <span>
                <Button
                  variant={hasChanges ? "success" : "secondary"}
                  size="medium"
                  icon={FaSave}
                  onClick={saveMacros}
                  disabled={!isGoldOrEnterprise || !hasChanges}
                >
                  Sauvegarder
                </Button>
              </span>
            </HoverTooltip>
            <Button
              variant="secondary"
              size="medium"
              icon={FaTimes}
              onClick={revertChanges}
              disabled={!hasChanges}
            >
              Annuler
            </Button>
          </div>
          {/* Search bar for filtering macros */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 12px 0' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par mot-clé ou contenu..."
              className="macro-input"
              style={{ flex: 1, fontFamily: "'Roboto', Arial, sans-serif" }}
            />
          </div>
          {/* Editor-only UI */}

          {(macros.length === 0) ? (
            <div className="macro-empty-state">
              <FaKeyboard className="macro-empty-icon" />
              <h3>Aucune macro configurée</h3>
              <p>Cliquez sur "Ajouter une macro" pour créer votre première macro</p>
            </div>
          ) : (
            <div className="macro-list" style={{ display: 'block' }}>
              {filteredMacros.map(({ m, idx }) => {
                const isExpanded = expandedIndex === idx;
                const truncated = (m.expansionText || '').length > 40 ? `${m.expansionText.slice(0, 40)}…` : (m.expansionText || '');
                return (
                  <div key={m.id || `macro-${idx}`} style={{ border: '1px solid #eee', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                    {/* Collapsed one-line row */}
                    <div
                      onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 10px', cursor: 'pointer', background: isExpanded ? 'rgba(220,20,60,0.05)' : 'transparent'
                      }}
                    >
                      <span style={{ fontFamily: 'monospace', color: '#c62828', minWidth: 120, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.keyword || '(nouveau)'}</span>
                      <span style={{ color: '#666', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{truncated || '—'}</span>
                      <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                        <span>
                          <Button
                            type="icon-only"
                            variant="danger"
                            size="small"
                            icon={FaTrash}
                            onClick={(e) => { e.stopPropagation(); deleteMacro(idx); }}
                            title="Supprimer cette macro"
                            disabled={!isGoldOrEnterprise}
                          />
                        </span>
                      </HoverTooltip>
                    </div>
                    {/* Expanded editor */}
                    {isExpanded && (
                      <div style={{ padding: '8px 10px', background: '#fff' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                          <label className="macro-label" style={{ minWidth: 80 }}>Mot-clé</label>
                          <input
                            type="text"
                            className="macro-input"
                            value={m.keyword}
                            onChange={e => updateKey(idx, e.target.value)}
                            maxLength={20}
                            placeholder="ex: email1, rapport2"
                            pattern="[a-zA-Z0-9]*"
                            style={{ flex: 1 }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <label className="macro-label" style={{ minWidth: 80 }}>Contenu</label>
                          <textarea
                            className="macro-textarea"
                            value={m.expansionText}
                            onChange={e => updateContent(idx, e.target.value)}
                            placeholder="Contenu qui remplacera le mot-clé..."
                            rows="3"
                            style={{ flex: 1 }}
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                          <Button type="inversed" variant="primary" size="small" onClick={() => setExpandedIndex(null)}>Terminer</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

export default MacroWindow;
